import { useState } from 'react';
import { Sparkles, X, Zap, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { resetDemoContent } from '@/utils/resetDemoContent';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useNativeConfirmation } from '@/hooks/useNativeConfirmation';
import { supabase } from '@/integrations/supabase/client';
interface DemoBannerProps {
  familyId: string;
  onReset?: () => void;
}
export function DemoBanner({
  familyId,
  onReset
}: DemoBannerProps) {
  const [isResetting, setIsResetting] = useState(false);
  const [isGoingLive, setIsGoingLive] = useState(false);
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const {
    showConfirmation
  } = useNativeConfirmation();
  const handleGoLive = async () => {
    const confirmed = await showConfirmation({
      title: 'Passer en mode réel ?',
      message: 'Votre contenu de démonstration sera conservé, mais ne sera plus marqué comme tel. Vous pourrez continuer à l\'utiliser normalement.',
      okButtonTitle: 'Passer en mode réel',
      cancelButtonTitle: 'Annuler'
    });
    if (!confirmed) return;
    setIsGoingLive(true);
    try {
      const {
        error
      } = await supabase.from('families').update({
        demo_content_active: false
      }).eq('id', familyId);
      if (error) throw error;

      // Invalidate all queries to refresh the UI
      await queryClient.invalidateQueries({
        queryKey: ['demo-mode']
      });
      toast({
        title: 'Mode réel activé',
        description: 'Votre contenu est maintenant en mode réel'
      });
      onReset?.();
    } catch (error) {
      console.error('Error going live:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de passer en mode réel',
        variant: 'destructive'
      });
    } finally {
      setIsGoingLive(false);
    }
  };
  const handleReset = async () => {
    const confirmed = await showConfirmation({
      title: 'Réinitialiser le contenu de démonstration ?',
      message: 'Cela supprimera toutes les tâches, événements, contacts et notes de démonstration. Vos membres de famille seront conservés.',
      okButtonTitle: 'Réinitialiser',
      cancelButtonTitle: 'Annuler'
    });
    if (!confirmed) return;
    setIsResetting(true);
    try {
      await resetDemoContent(familyId);

      // Invalidate all queries to refresh the UI
      await queryClient.invalidateQueries();
      toast({
        title: 'Contenu réinitialisé',
        description: 'Le contenu de démonstration a été supprimé avec succès'
      });
      onReset?.();
    } catch (error) {
      console.error('Error resetting demo content:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de réinitialiser le contenu',
        variant: 'destructive'
      });
    } finally {
      setIsResetting(false);
    }
  };
  return <div className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-gradient-to-r from-amber-500/20 via-orange-400/20 to-amber-500/20 border-b border-amber-300/30 px-4 py-3 shadow-lg shadow-amber-500/10">
      <div className="absolute inset-0 bg-gradient-to-r from-amber-400/10 to-orange-400/10" />
      <div className="relative flex items-center justify-between gap-3 max-w-screen-xl mx-auto">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Sparkles className="h-4 w-4 text-amber-600 dark:text-amber-300 flex-shrink-0 animate-pulse" />
          <span className="text-sm font-medium text-amber-900 dark:text-amber-100 truncate">
            Mode démo
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Primary action: Go Live - Stripe style */}
          <Button size="sm" onClick={handleGoLive} disabled={isGoingLive} className="flex-shrink-0 h-8 px-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium shadow-md hover:shadow-lg transition-all">
            <Zap className="h-3.5 w-3.5 mr-1.5" />
            <span className="hidden sm:inline">Passer en mode réel</span>
            <span className="sm:hidden">Go live </span>
          </Button>
          
          {/* Secondary action: Reset */}
          
        </div>
      </div>
    </div>;
}